/**
 * The Restock Rocket app injects an "Inquire Now" / "Notify me" button
 * directly inside `.product-item__image-wrapper` (overlaid on the product
 * photo), instead of in the normal `.product-item__cta-wrapper` slot below
 * the title/price/reviews that every other card's "Add to cart" button
 * lives in. Worse, it does this even for products that already have a
 * working add-to-cart form -- so that card ends up with the image-overlay
 * button PLUS a separate normal "Add to cart" button below, both offering
 * conflicting actions on the same in-stock item.
 *
 * Behavior:
 *  - If the card already has a working add-to-cart form (product is
 *    actually purchasable), the app's button is redundant -- remove it so
 *    the card matches every other card in the grid exactly.
 *  - If there's no working add-to-cart form (genuinely unavailable
 *    product), relocate the app's button (the same node, not a clone, so
 *    its own click handlers keep working) into a wrapper matching the
 *    normal CTA slot and give it the same button classes, so it's
 *    positioned and styled like every other card's button instead of
 *    floating over the image.
 *
 * A MutationObserver is required because the app injects its button
 * asynchronously after its own script loads.
 */
(() => {
  function handle(button) {
    if (button.dataset.cyanHandled) return;
    button.dataset.cyanHandled = 'true';

    const productItem = button.closest('.product-item');
    const info = productItem && productItem.querySelector('.product-item__info');
    if (!info) return;

    const hasWorkingAddToCart = !!info.querySelector('.product-item__cta-wrapper form[action*="/cart/add"]');

    if (hasWorkingAddToCart) {
      button.remove();
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.className = 'product-item__cta-wrapper';
    button.classList.add('product-item__cta', 'button', 'button--primary');
    wrapper.appendChild(button);
    info.appendChild(wrapper);
  }

  function scan(root) {
    root.querySelectorAll('.restock-rocket-button').forEach(handle);
  }

  scan(document);

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('.restock-rocket-button')) {
          handle(node);
        } else if (node.querySelectorAll) {
          scan(node);
        }
      });
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
